import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { UIStateEnum } from '../../../enum/UIStateEnum';
import { ErrorCategoryService } from '../../../services/error-category.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ErrorHandlingService } from '../../../services/error-handling.service';
import { DialogBaseComponent } from '../dialog-base/dialog-base.component';
import { IErrorCategory } from '../../../interfaces/error/IErrorCategory';

export interface EditErrorCategoryTranslationDialogData {
  category: IErrorCategory;
  languageCode?: string;
}

@Component({
  selector: 'app-edit-error-category-translation',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    DialogBaseComponent
  ],
  templateUrl: './edit-error-category-translation.component.html',
  styleUrls: ['./edit-error-category-translation.component.scss']
})
export class EditErrorCategoryTranslationComponent {
  public uiState: UIStateEnum = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;

  public languageCodeControl = new FormControl<string>('en', [Validators.required]);
  public messageControl = new FormControl<string>('', [Validators.required, Validators.minLength(1)]);

  public translationForm = new FormGroup({
    languageCode: this.languageCodeControl,
    message: this.messageControl
  });

  public availableLanguages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'French' }
  ];

  public isEditing = false;
  public dialogTitle = '';
  public selectableLanguages: { code: string; name: string }[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditErrorCategoryTranslationDialogData,
    protected dialogRef: MatDialogRef<EditErrorCategoryTranslationComponent>,
    private errorCategoryService: ErrorCategoryService,
    private snackBarService: SnackbarService,
    private errorHandlingService: ErrorHandlingService
  ) {
    this.initializeForm();
    this.dialogTitle = this.computeDialogTitle();
    this.selectableLanguages = this.computeSelectableLanguages();
  }

  private initializeForm(): void {
    if (this.data.languageCode) {
      this.isEditing = true;
      this.languageCodeControl.setValue(this.data.languageCode);
      this.languageCodeControl.disable();

      const existingMessage = this.data.category.Translations[this.data.languageCode];
      if (existingMessage) {
        this.messageControl.setValue(existingMessage);
      }
    }
  }

  private computeDialogTitle(): string {
    if (this.isEditing) {
      const langName = this.availableLanguages.find(l => l.code === this.data.languageCode)?.name || this.data.languageCode?.toUpperCase();
      return `Edit ${langName} Translation`;
    }
    return 'Add Translation';
  }

  private computeSelectableLanguages(): { code: string; name: string }[] {
    if (this.isEditing) {
      return this.availableLanguages;
    }
    return this.availableLanguages.filter(lang =>
      !Object.keys(this.data.category.Translations).includes(lang.code)
    );
  }

  public saveTranslation(): void {
    if (this.translationForm.valid) {
      this.uiState = UIStateEnum.ShowLoading;

      const languageCode = this.languageCodeControl.getRawValue() || 'en';

      this.errorCategoryService.updateCategoryTranslation(this.data.category.Id, {
        LanguageCode: languageCode,
        Message: this.messageControl.value || ''
      }).subscribe({
        next: () => {
          const action = this.isEditing ? 'updated' : 'added';
          this.snackBarService.openSuccessfulSnackBar(`Translation ${action} successfully`);
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.uiState = UIStateEnum.ShowData;
          this.errorHandlingService.displayDialogLevelErrorMessage(err, true);
        }
      });
    }
  }
}
